import { REDUCER_ACTION_TYPES } from '@federa/const/reducer-actions';
import { DEDUP_WINDOWS } from '@federa/const/timing';
import { SENTINEL_UNKNOWN } from '@federa/const/sentinels';
import { generateUuid } from '@federa/utils/crypto/uuid';
import { sortByTimestampDesc, sortByCreatedAtAsc, extractTimestamp } from '@federa/utils/dates/timestamp';
import { formatDateTimeISO } from '@federa/utils/dates/iso-arithmetic-core';
import { parseUserId } from '@federa/utils/strings/parse';
import type { AgentStatusFrame, ChatMessageStruct, ChatParticipantStruct, ChatRoomStruct } from '@federa/types/conversations-chat-structs';
import type { ConversationId } from '@federa/types/primitives-identifiers';
import type { AppDispatch as DispatchFn } from '@federa/types/react-app-state';
import type { ChatFileAttachment as FileAttachment, MessagingRefs } from '@federa/types/ui-chat-messaging';


/** The room list in the order every room surface shows it: most recent message first, read off the numeric
 *  `lastMessageTimestamp` with the ISO `lastMessageTime` as its fallback. */
export const sortRooms = (rooms: ChatRoomStruct[]) =>
	sortByTimestampDesc(rooms, (r) => extractTimestamp(r.lastMessageTimestamp, r.lastMessageTime));

function deduplicateMessages(messages: ChatMessageStruct[], msg: ChatMessageStruct) {
	return messages.filter((item) => {
		if (item.id === msg.id) return false;
		const isMatchingOptimistic =
			item.id.startsWith('optimistic-') &&
			item.senderId === msg.senderId &&
			item.content === msg.content &&
			Math.abs(new Date(item.createdAt).getTime() - new Date(msg.createdAt).getTime()) < DEDUP_WINDOWS.wsMessage;
		return !isMatchingOptimistic;
	});
}

function updateRoomWithMessage(room: ChatRoomStruct, msg: ChatMessageStruct, ts: number): ChatRoomStruct {
	return {
		...room,
		lastMessage: msg.content,
		lastMessageId: msg.id,
		lastMessageTime: msg.createdAt,
		lastMessageTimestamp: ts,
	};
}

/**
 * Commit a message list to BOTH readers -- the ref the next merge reads and the reducer the render reads.
 *
 * THE PAIRING IS THE DECISION, and it is why this is one function rather than two statements repeated. The ref is the
 * live accumulator every subsequent merge dedupes against; the reducer state is what the thread renders. Writing one
 * without the other is silent in both directions: a ref-only write renders a stale thread while the merge base moves
 * on, and a dispatch-only write renders the message and then loses it on the next incoming frame, because the merge
 * still starts from the list the ref never received. Neither shows up as an error -- they show up as a message that
 * flickers or never arrives, which is why the two writes are not allowed to sit apart.
 */
function commitMessages(refs: MessagingRefs, dispatch: DispatchFn, messages: ChatMessageStruct[]): void {
	refs.messages.current = messages;
	dispatch({ type: REDUCER_ACTION_TYPES.MESSAGING_SET_MESSAGES, payload: messages });
}

/** The ROOM-list half of {@link commitMessages}, with the same contract for the same reason: `refs.rooms.current` is
 *  the base the next incoming frame re-sorts from, and the reducer state is what the room list renders. */
function commitRooms(refs: MessagingRefs, dispatch: DispatchFn, rooms: ChatRoomStruct[]): void {
	refs.rooms.current = rooms;
	dispatch({ type: REDUCER_ACTION_TYPES.MESSAGING_SET_ROOMS, payload: rooms });
}

function handleIncomingMessage(
	message: ChatMessageStruct,
	options: {
		raw: { unread_count?: number };
		refs: MessagingRefs;
		dispatch: DispatchFn;
	},
) {
	const { raw, refs, dispatch } = options;
	const msg = {
		...message,
		senderId: parseUserId(message.senderId || SENTINEL_UNKNOWN),
	};
	commitMessages(refs, dispatch, sortByCreatedAtAsc([...deduplicateMessages(refs.messages.current, msg), msg]));

	const ts = new Date(msg.createdAt).getTime();
	const rooms = refs.rooms.current.map((r) => (r.id === msg.roomId ? updateRoomWithMessage(r, msg, ts) : r));
	commitRooms(refs, dispatch, sortRooms(rooms));

	const activeRoom = refs.currentRoom.current;
	if (activeRoom?.id === msg.roomId) {
		// The ref IS the live current-room read path (useLatestRef over the `room` prop).
		refs.currentRoom.current = updateRoomWithMessage(activeRoom, msg, ts);
	}

	if (typeof raw.unread_count === 'number') {
		const withUnread = rooms.map((r) => (r.id === msg.roomId ? { ...r, unreadCount: raw.unread_count } : r));
		commitRooms(refs, dispatch, sortRooms(withUnread));
	}
}

export function createOptimisticMessage(
	content: string,
	options: {
		roomId: string;
		userId: string;
		username: string;
		files?: FileAttachment[];
	},
): ChatMessageStruct {
	const { roomId, userId, username, files } = options;
	const attachments = files?.map((f) => ({ url: 'url' in f ? f.url : '', name: f.name, type: f.type })).filter((a) => a.url);
	return {
		id: `optimistic-${generateUuid()}`,
		content: content.trim(),
		senderId: parseUserId(userId),
		senderName: username || 'You',
		createdAt: formatDateTimeISO(null),
		roomId: roomId as ConversationId,
		attachments: attachments && attachments.length > 0 ? attachments : undefined,
	};
}

export function dispatchOptimistic(msg: ChatMessageStruct, options: { refs: MessagingRefs; dispatch: DispatchFn; timeoutMs: number }) {
	const { refs, dispatch, timeoutMs } = options;
	commitMessages(refs, dispatch, sortByCreatedAtAsc([...refs.messages.current.filter((m) => m.id !== msg.id), msg]));

	const timeoutId = setTimeout(() => {
		const filtered = refs.messages.current.filter((m) => m.id !== msg.id);
		if (filtered.length !== refs.messages.current.length) {
			commitMessages(refs, dispatch, filtered);
		}
		refs.timeoutIds.current = refs.timeoutIds.current.filter((id) => id !== timeoutId);
	}, timeoutMs);
	refs.timeoutIds.current.push(timeoutId);
}

/** `held` with every member of `incoming` laid over it by id: an incoming row wins its id, a held row nothing came for stays. */
function mergeById(held: readonly ChatMessageStruct[], incoming: readonly ChatMessageStruct[]): ChatMessageStruct[] {
	const byId = new Map<string, ChatMessageStruct>();
	for (const message of held) byId.set(message.id, message);
	for (const message of incoming) byId.set(message.id, message);
	return [...byId.values()];
}

export function createClientHandlers(refs: MessagingRefs, dispatch: DispatchFn) {
	return {
		// Through `commitMessages`, not a bare dispatch: a history load REPLACES the list every later merge starts from,
		// and the frame that follows it can arrive inside the same event-handler invocation (`createMessageHandler` runs
		// `handleMessagesPayload` and then falls through to `handleChatMessage`), so the ref must move with the render.
		// MERGED by id over what is already held, not a replacement. The newest group reaches the pane twice on open --
		// the HTTP page the pane fetches and the socket's session-init backfill -- and whichever lands second used to
		// replace the list, which also threw away any older pages the reader had scrolled back to before a reconnect.
		onHistory: (history: ChatMessageStruct[]) => commitMessages(refs, dispatch, sortByCreatedAtAsc(mergeById(refs.messages.current, history))),
		onParticipants: (participants: ChatParticipantStruct[]) =>
			dispatch({
				type: REDUCER_ACTION_TYPES.MESSAGING_UPDATE,
				payload: { currentParticipants: participants },
			}),
		onParticipantJoin: (p: ChatParticipantStruct) => {
			if (!refs.participants.current.find((x) => x.id === p.id)) {
				dispatch({
					type: REDUCER_ACTION_TYPES.MESSAGING_UPDATE,
					payload: { currentParticipants: [...refs.participants.current, p] },
				});
			}
		},
		onParticipantLeave: (id: string) =>
			dispatch({
				type: REDUCER_ACTION_TYPES.MESSAGING_UPDATE,
				payload: {
					currentParticipants: refs.participants.current.filter((x) => x.id !== id),
				},
			}),
		onMessage: (msg: ChatMessageStruct, raw: { unread_count?: number }) => {
			handleIncomingMessage(msg, {
				raw,
				refs,
				dispatch,
			});
			// A real assistant message arrived -- clear the ephemeral typing/tool-status row.
			dispatch({ type: REDUCER_ACTION_TYPES.MESSAGING_SET_AGENT_STATUS, payload: null });
		},
		onAgentStatus: (frame: AgentStatusFrame) => {
			// Terminal stages and empty labels drop the indicator; everything else surfaces a label.
			const isTerminal = frame.stage === 'complete' || frame.stage === 'error' || frame.stage === 'cancelled';
			const payload = isTerminal || !frame.label ? null : frame;
			dispatch({ type: REDUCER_ACTION_TYPES.MESSAGING_SET_AGENT_STATUS, payload });
		},
	};
}
